/// <amd-module name="Build/Run/Components/Canvas/Graph" />
import { Edge } from "Build/Run/Components/Canvas/Edge";
import { GraphLayoutHelper, IGraph, IStagingOrderAndDependencies } from "Build/Run/Components/Canvas/GraphLayoutHelper";
import {
    Constants,
    EdgeMatrix,
    IEdgeDataForLayout,
    IGraphComponent,
    IGraphListComponent,
    IGraphProps,
    IGraphSize,
    INodeData,
    INodeDataForLayout,
    IPoint
} from "Build/Run/Components/Canvas/Types";
import * as React from "react";
import "VSS/LoaderPlugins/Css!DistributedTaskControls/Components/Canvas/Graph";
import { VssComponent } from "VSS/Platform/Layout";

export interface IGraphListState {
    incomingEdges: JSX.Element[];
    incomingEdgesEvaluated: boolean;
}

type IDictionaryStringTo<T> = { [key: string]: T };

export class GraphList extends VssComponent<IGraphProps, IGraphListState> implements IGraphListComponent {
    constructor(props: IGraphProps) {
        super(props);
        this.state = {
            incomingEdges: [],
            incomingEdgesEvaluated: false
        };
    }

    public componentWillMount() {
        this._disjointGraphs = GraphLayoutHelper.splitGraph(this.props.nodes, this.props.edges);
        this._graphComponents = [];
    }

    public componentWillReceiveProps(nextProps: IGraphProps) {
        this._disjointGraphs = GraphLayoutHelper.splitGraph(nextProps.nodes, nextProps.edges);
        this.setState({
            incomingEdgesEvaluated: false
        });

        this._graphComponents = [];
    }

    public getGraphComponents(): Graph[] {
        return this._graphComponents;
    }

    public render(): JSX.Element {
        let graphElements: JSX.Element[] = [];
        this._disjointGraphs.forEach((graph: IGraph, index) => {
            graphElements.push(
                <Graph
                    ref={graph => graph && this._graphComponents.push(graph)}
                    key={graph.nodes[0].key}
                    {...this.props}
                    nodes={graph.nodes}
                    edges={graph.edges}
                    leftMargin={0}
                />
            );
        });

        const width = this._getGraphLeftMargin();
        const height = this._getHeight();
        return (
            <div className="dtc-graph-list-parent">
                {width && width > 0 ? (
                    <div className="dtc-incoming-edge-container" style={{ width: width, height: height }} aria-hidden="true">
                        <svg className="dtc-incoming-edge-canvas">{this.state.incomingEdges}</svg>
                    </div>
                ) : null}
                <div className="dtc-disjoint-graph-container">{graphElements}</div>
            </div>
        );
    }

    public componentDidMount() {
        this._evaluateAndSetIncomingEdges();
    }

    public componentDidUpdate() {
        this._evaluateAndSetIncomingEdges();
    }

    private _evaluateAndSetIncomingEdges() {
        if (!this.state.incomingEdgesEvaluated) {
            const edgeDataForLayout = GraphLayoutHelper.getIncomingEdgeLayouts(this.props, this._graphComponents);
            const edgeElements = edgeDataForLayout.map(edgeData => {
                return (
                    <Edge
                        from={edgeData.from}
                        to={edgeData.to}
                        fromDepth={edgeData.fromDepth}
                        quadraticBezierVerticalDisplacement={this._getQuadraticBezierVerticalDisplacement()}
                        key={edgeData.key}
                    />
                );
            });

            this.setState({
                incomingEdgesEvaluated: true,
                incomingEdges: edgeElements
            });
        }
    }

    private _getQuadraticBezierVerticalDisplacement(): number {
        return this.props.nodeHeight / Constants.edgeQuadraticBezierScaleDownFactor;
    }

    private _getGraphLeftMargin(): number {
        return GraphLayoutHelper.resolveGraphLeftMargin(this.props.leftMargin);
    }

    private _getHeight(): number {
        let height: number = 0;
        if (this._graphComponents) {
            for (let component of this._graphComponents) {
                height += component.getGraphSize().height;
            }
        }
        return height;
    }

    private _disjointGraphs: IGraph[] = [];
    private _graphComponents: Graph[] = [];
}

export class Graph extends VssComponent<IGraphProps, {}> implements IGraphComponent {
    public componentWillMount() {
        this._validateNodesAndEdges(this.props.nodes, this.props.edges);
        this._stagingOrderAndDependencies = GraphLayoutHelper.createStagingOrderAndDependencies(this.props.nodes, this.props.edges);
    }

    public componentWillReceiveProps(nextProps: IGraphProps) {
        this._validateNodesAndEdges(nextProps.nodes, nextProps.edges);
        this._stagingOrderAndDependencies = GraphLayoutHelper.createStagingOrderAndDependencies(nextProps.nodes, nextProps.edges);
        this._identifiedAncestors = {};
    }

    private _nodesComparer = (s: INodeData, t: INodeData): boolean => {
        return s.key === t.key && s.nodeRankHint === t.nodeRankHint && s.nodeHeightHint === t.nodeHeightHint && s.friendlyName === t.friendlyName;
    };

    public getStagingOrder(): INodeData[][] {
        return this._stagingOrderAndDependencies ? this._stagingOrderAndDependencies.stagingOrder : [];
    }

    public getGraphSize(): IGraphSize {
        return { height: this._svgHeight, width: this._svgWidth };
    }

    public getPositionOfIncomingNodes(): IPoint[] {
        return this._incomingNodeRelativePositions;
    }

    public render(): JSX.Element {
        const stagingOrderAndDependencies = this._stagingOrderAndDependencies;
        const stagingOrder = stagingOrderAndDependencies.stagingOrder;

        let dependencies = stagingOrderAndDependencies.dependencies;
        let layoutError = stagingOrderAndDependencies.layoutError;

        // Get the max bottom right position of the node that is drawn. This is used to set the height and width of svg.
        let maxBottomRight: IPoint = { x: 0, y: 0 };
        let nodeElements: JSX.Element[] = this._getNodeElements(stagingOrder, maxBottomRight);
        let edgeElements: JSX.Element[] = [];
        if (!layoutError) {
            // If there are layout errors, do not bother drawing any edges.
            edgeElements = this._getEdgeElements(stagingOrder, dependencies);
        }

        const graphLeftMargin = this._getGraphLeftMargin();
        this._svgWidth = maxBottomRight.x + this.props.horizontalMargin + graphLeftMargin;
        this._svgHeight = maxBottomRight.y + this.props.verticalMargin;

        if (!layoutError) {
            this._populateIncomingNodeIndices(stagingOrder[0]);
        } else {
            this._incomingNodeRelativePositions = [];
        }

        return (
            <div className="dtc-canvas-container">
                {layoutError && <div>{layoutError}</div>}
                <div className="dtc-graph-container">
                    <svg className="svg-surface" height={this._svgHeight + "px"} width={this._svgWidth + "px"} focusable="false" aria-hidden="true">
                        {edgeElements}
                    </svg>
                    {nodeElements}
                </div>
            </div>
        );
    }

    private _populateIncomingNodeIndices(firstStage: INodeDataForLayout[]): void {
        const incomingNodesMap = this.props.edges[""];
        this._incomingNodeRelativePositions = [];
        if (incomingNodesMap) {
            firstStage.forEach((node, index) => {
                if (incomingNodesMap[node.key]) {
                    this._incomingNodeRelativePositions.push({
                        x: node.midPoints.left.x,
                        y: node.midPoints.left.y
                    });
                }
            });
        }
    }

    private _validateNodesAndEdges(nodes: INodeData[], edges: EdgeMatrix) {
        GraphLayoutHelper.validateNodesAndEdges(nodes, edges);
    }

    private _getNodeElements(stagingOrder: INodeDataForLayout[][], /* out */ maxBottomRight: IPoint): JSX.Element[] {
        const graphLeftMargin = this._getGraphLeftMargin();
        let nodeListForLayout = GraphLayoutHelper.getNodeDataForLayout(stagingOrder, this.props, graphLeftMargin, maxBottomRight);

        return nodeListForLayout.map((nodeDataForLayout: INodeDataForLayout) => {
            return this._getNodeElement(nodeDataForLayout, nodeDataForLayout.nodeHeightHint || this.props.nodeHeight, this.props.nodeWidth);
        });
    }

    private _getEdgeElements(stagingOrder: INodeDataForLayout[][], dependencies: IDictionaryStringTo<string[]>): JSX.Element[] {
        let edgeListForLayout = GraphLayoutHelper.getEdgeDataForLayout(
            stagingOrder,
            dependencies,
            this.props.edges,
            this.props,
            this.props.selectedNodeKey,
            this._identifiedAncestors
        );

        return edgeListForLayout.map((edgeDataForLayout: IEdgeDataForLayout) => {
            return (
                <Edge
                    from={edgeDataForLayout.from}
                    intermediate1={edgeDataForLayout.intermediate1}
                    intermediate2={edgeDataForLayout.intermediate2}
                    to={edgeDataForLayout.to}
                    key={edgeDataForLayout.key}
                    fromDepth={edgeDataForLayout.fromDepth}
                    quadraticBezierVerticalDisplacement={this._getQuadraticBezierVerticalDisplacement()}
                />
            );
        });
    }

    private _getNodeElement(node: INodeDataForLayout, height: number, width: number): JSX.Element {
        const style: React.CSSProperties = {
            position: "absolute",
            height: height,
            width: width,
            top: node.topLeftPosition ? node.topLeftPosition.y : 0,
            left: node.topLeftPosition ? node.topLeftPosition.x : 0
        };

        return (
            <div className="div-node" style={style} key={node.key}>
                {node.getNodeElement && node.getNodeElement(node.key, node.data)}
            </div>
        );
    }

    private _getGraphLeftMargin(): number {
        return GraphLayoutHelper.resolveGraphLeftMargin(this.props.leftMargin);
    }

    private _getQuadraticBezierVerticalDisplacement(): number {
        return this.props.nodeHeight / Constants.edgeQuadraticBezierScaleDownFactor;
    }

    private _identifiedAncestors: IDictionaryStringTo<string[]> = {};
    private static s_telemetryKeyTracker: IDictionaryStringTo<boolean> = {};
    private _stagingOrderAndDependencies: IStagingOrderAndDependencies;
    private _svgWidth: number;
    private _svgHeight: number;
    private _incomingNodeRelativePositions: IPoint[] = [];
}
